/**
 * Build an iOS- and Android-camera friendly vCard payload for QR codes.
 *
 * Why we have this helper:
 *  - vCard 3.0 (RFC 6350) MANDATES CRLF line endings. iOS Camera silently
 *    falls back to scanning the data as a URL when CRLF is missing, which
 *    is why "Save contact" QRs were opening the doctor's website instead
 *    of triggering "Add to Contacts".
 *  - Field values containing `,` `;` `\` MUST be escaped, else the parser
 *    treats them as field separators and the resulting contact is malformed.
 *  - We deliberately DROP non-standard `X-WHATSAPP:` lines — they embed a
 *    raw `https://wa.me/...` URL inside the vCard data which some scanners
 *    pick up as the primary action.
 *  - We keep the contact card small (single ADR, no URL clutter) so phone
 *    cameras can decode reliably even on dense backgrounds.
 *
 * Input: `doctor` object (name, qualifications, phone, locations[])
 *        `siteUrl` (optional — included as URL field for record only)
 *
 * Output: a well-formed vCard 3.0 string ready to be encoded as a QR.
 */
function escapeVcardValue(v) {
  if (v == null) return '';
  return String(v)
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function splitNameForN(fullName) {
  // vCard `N:` field is `Family;Given;Additional;Prefix;Suffix`
  // Doctor names are typically "Dr. Chetna Tikoo" or "Chetna Tikoo".
  const raw = (fullName || '').replace(/^Dr\.?\s+/i, '').trim();
  const parts = raw.split(/\s+/);
  if (parts.length === 1) return { given: parts[0] || '', family: '' };
  const family = parts.pop();
  const given = parts.join(' ');
  return { given, family };
}

export function buildVcard(doctor, siteUrl, opts = {}) {
  if (!doctor) return '';
  const { includePhone = false } = opts;
  const { given, family } = splitNameForN(doctor.name);
  const fn = (doctor.name || '').trim();
  const phoneRaw = (doctor.phone || '').replace(/\s+/g, '');
  const phoneE164 = phoneRaw.startsWith('+') ? phoneRaw : (phoneRaw ? `+91${phoneRaw.replace(/\D/g, '').slice(-10)}` : '');

  const lines = [];
  lines.push('BEGIN:VCARD');
  lines.push('VERSION:3.0');
  lines.push(`N:${escapeVcardValue(family)};${escapeVcardValue(given)};;Dr.;`);
  lines.push(`FN:${escapeVcardValue(fn)}`);
  if (doctor.qualifications) lines.push(`TITLE:${escapeVcardValue(doctor.qualifications)}`);
  // PRIVACY DEFAULT: phone is NOT embedded unless the doctor explicitly
  // opted in (via the Premium+ "Share phone in QR" toggle in Clinic
  // Branding). Keeps the in-app Chat / Voice Call modules valuable —
  // patients still have to reach the doctor through the platform.
  if (includePhone && phoneE164) lines.push(`TEL;TYPE=CELL,VOICE:${phoneE164}`);

  // Single primary location only — multi-ADR cards bloat the QR density and
  // hurt scan-success rates on dim posters. The website link surfaces the rest.
  const primary = Array.isArray(doctor.locations) && doctor.locations[0];
  if (primary) {
    const street = escapeVcardValue(primary.address || '');
    const city = escapeVcardValue(primary.city || '');
    const region = escapeVcardValue(primary.state || '');
    const postal = escapeVcardValue(primary.pincode || '');
    const country = escapeVcardValue(primary.country || 'India');
    // ADR: PO Box;Extended;Street;City;Region;Postal;Country
    lines.push(`ADR;TYPE=WORK:;;${street};${city};${region};${postal};${country}`);
  }

  if (siteUrl) lines.push(`URL:${escapeVcardValue(siteUrl)}`);

  lines.push('END:VCARD');
  // RFC 6350 mandates CRLF — iOS Camera quietly refuses LF-only vCards and
  // falls back to URL-mode, which is exactly the bug we're fixing.
  return lines.join('\r\n');
}
